import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Separator } from '../components/ui/separator';
import {
  CreditCard,
  Lock,
  CheckCircle,
  Shield,
  Clock,
  ArrowLeft,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = ({ user }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiry: '',
    cvc: '',
    name: ''
  });

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/courses/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setCourse(data);
        }
      } catch (error) {
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCourse();
  }, [slug]);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: `/checkout/${slug}` } });
    }
  }, [user, navigate, slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400">Course not found</p>
          <Button onClick={() => navigate('/courses')} className="mt-4">
            Browse Courses
          </Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    // Simulate payment processing (replace with real Stripe integration later)
    setTimeout(async () => {
      try {
        // After successful payment, enroll the user
        const response = await fetch(`${BACKEND_URL}/api/enrollments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ course_id: course.id })
        });
        
        if (response.ok) {
          // Successfully enrolled - redirect to learning page
          navigate(`/learn/${course.slug}`, { 
            state: { message: 'Payment successful! Welcome to the course.' } 
          });
        } else {
          const error = await response.json();
          if (error.detail === 'Already enrolled in this course') {
            navigate(`/learn/${course.slug}`);
          } else {
            alert(error.detail || 'Failed to complete enrollment');
          }
        }
      } catch (error) {
        console.error('Enrollment error:', error);
        alert('Payment processing failed. Please try again.');
      } finally {
        setIsProcessing(false);
      }
    }, 2000);
  };

  const originalPrice = course.original_price || course.price * 1.5;
  const discount = originalPrice - course.price;
  const discountPercent = Math.round((discount / originalPrice) * 100);

  // Count total lessons
  const totalLessons = course.sections?.reduce((acc, section) => acc + (section.lessons?.length || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to course
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Checkout Form */}
          <div>
            <h1 className="text-2xl font-bold text-white mb-6">Checkout</h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Payment Method Selection */}
              <Card className="bg-gray-900/50 border-gray-800">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Payment Method</h3>
                  <RadioGroup
                    value={paymentMethod}
                    onValueChange={setPaymentMethod}
                    className="space-y-3"
                  >
                    <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      paymentMethod === 'card' 
                        ? 'bg-amber-500/10 border-amber-500' 
                        : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                    }`}>
                      <RadioGroupItem value="card" id="card" className="border-gray-600" />
                      <Label htmlFor="card" className="flex items-center gap-2 cursor-pointer flex-1">
                        <CreditCard className="w-5 h-5 text-gray-400" />
                        <span className="text-white">Credit / Debit Card</span>
                      </Label>
                      <div className="flex gap-1">
                        <img src="https://logo.clearbit.com/visa.com" alt="Visa" className="h-6 w-auto" />
                        <img src="https://logo.clearbit.com/mastercard.com" alt="Mastercard" className="h-6 w-auto" />
                      </div>
                    </div>
                    <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      paymentMethod === 'paypal' 
                        ? 'bg-amber-500/10 border-amber-500' 
                        : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                    }`}>
                      <RadioGroupItem value="paypal" id="paypal" className="border-gray-600" />
                      <Label htmlFor="paypal" className="flex items-center gap-2 cursor-pointer flex-1">
                        <img src="https://logo.clearbit.com/paypal.com" alt="PayPal" className="h-5 w-auto" />
                        <span className="text-white">PayPal</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>

              {/* Card Details */}
              {paymentMethod === 'card' && (
                <Card className="bg-gray-900/50 border-gray-800">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-white mb-2">Card Details</h3>
                    
                    <div>
                      <Label htmlFor="cardName" className="text-gray-400">Name on Card</Label>
                      <Input
                        id="cardName"
                        placeholder="John Smith"
                        value={cardDetails.name}
                        onChange={(e) => setCardDetails({ ...cardDetails, name: e.target.value })}
                        className="mt-1 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-amber-500"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="cardNumber" className="text-gray-400">Card Number</Label>
                      <Input
                        id="cardNumber"
                        placeholder="4242 4242 4242 4242"
                        value={cardDetails.number}
                        onChange={(e) => setCardDetails({ ...cardDetails, number: e.target.value })}
                        className="mt-1 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-amber-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="expiry" className="text-gray-400">Expiry Date</Label>
                        <Input
                          id="expiry"
                          placeholder="MM/YY"
                          value={cardDetails.expiry}
                          onChange={(e) => setCardDetails({ ...cardDetails, expiry: e.target.value })}
                          className="mt-1 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-amber-500"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="cvc" className="text-gray-400">CVC</Label>
                        <Input
                          id="cvc"
                          placeholder="123"
                          value={cardDetails.cvc}
                          onChange={(e) => setCardDetails({ ...cardDetails, cvc: e.target.value })}
                          className="mt-1 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-amber-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Lock className="w-4 h-4" />
                      <span>Your payment information is encrypted and secure</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* PayPal Info */}
              {paymentMethod === 'paypal' && (
                <Card className="bg-gray-900/50 border-gray-800">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">
                      You will be redirected to PayPal to complete your payment securely.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-amber-600 hover:bg-amber-500 py-6 text-lg"
                disabled={isProcessing}
                data-testid="checkout-submit-btn"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Complete Purchase - ${course.price}
                  </>
                )}
              </Button>

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  <span>Secure Checkout</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>30-Day Guarantee</span>
                </div>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="bg-gray-900/50 border-gray-800 sticky top-24">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>

                {/* Course Preview */}
                <div className="flex gap-4 mb-6">
                  <div className="w-24 h-16 rounded-lg overflow-hidden shrink-0 bg-gray-800">
                    <img
                      src={course.thumbnail || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400'}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-medium line-clamp-2">{course.title}</h4>
                    <p className="text-gray-500 text-sm">{course.level}</p>
                  </div>
                </div>

                <Separator className="bg-gray-800 mb-6" />

                {/* Price Breakdown */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-gray-400">
                    <span>Original Price</span>
                    <span className="line-through">${originalPrice.toFixed(2)}</span>
                  </div>
                  {discountPercent > 0 && (
                    <div className="flex items-center justify-between text-emerald-400">
                      <span>Discount ({discountPercent}% off)</span>
                      <span>-${discount.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                <Separator className="bg-gray-800 mb-6" />

                <div className="flex items-center justify-between text-xl font-semibold mb-6">
                  <span className="text-white">Total</span>
                  <span className="text-amber-400">${course.price}</span>
                </div>

                {/* What's Included */}
                <div className="bg-gray-800/30 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">What's included:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>{course.duration || 'Hours'} of video content</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>{totalLessons} lessons</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>Full lifetime access</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>Certificate of completion</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>30-day money-back guarantee</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
